import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Bell, Mail, Clock, Check, AlertTriangle, User, Package, 
  Calendar, Send, Trash2, Loader2, Plus
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, formatDateTime } from '../utils/dateFormat';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomerReminders = () => {
  const { getAuthHeader } = useAuth();
  const [reminders, setReminders] = useState([]);
  const [dueReminders, setDueReminders] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  const [reminderForm, setReminderForm] = useState({
    customer_id: '',
    customer_name: '',
    product_id: '',
    product_name: '',
    quantity: 1,
    supply_type: '30_days',
    custom_days: 30,
    notify: 'me'
  });
  const [customerQuery, setCustomerQuery] = useState('');
  const [productQuery, setProductQuery] = useState('');
  const [productResults, setProductResults] = useState([]);

  const fetchData = async () => {
    try {
      // NOTE: no product catalogue fetch here — products are searched
      // server-side as you type (the old 15,000-product download made this
      // page painfully slow)
      const [remindersRes, dueRes, customersRes] = await Promise.all([
        axios.get(`${API_URL}/reminders`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/reminders/due`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/customers`, { headers: getAuthHeader() }),
      ]);
      setReminders(remindersRes.data);
      setDueReminders(dueRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Debounced server-side product search
  useEffect(() => {
    if (!productQuery.trim() || reminderForm.product_id) {
      setProductResults([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API_URL}/products?limit=15&search=${encodeURIComponent(productQuery.trim())}`, { headers: getAuthHeader() });
        setProductResults(res.data || []);
      } catch { setProductResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [productQuery, reminderForm.product_id]);

  const customerSuggestions = customerQuery.trim() && !reminderForm.customer_id
    ? customers.filter(c => (c.name || '').toLowerCase().includes(customerQuery.trim().toLowerCase())).slice(0, 8)
    : [];

  const handleCreateReminder = async (e) => {
    e.preventDefault();
    if (!reminderForm.customer_id && !customerQuery.trim()) {
      toast.error('Type a customer name or pick one from the list');
      return;
    }
    if (!reminderForm.product_id && !productQuery.trim()) {
      toast.error('Type or pick a product');
      return;
    }

    setFormLoading(true);
    try {
      const res = await axios.post(`${API_URL}/reminders`, {
        customer_id: reminderForm.customer_id || null,
        customer_name: reminderForm.customer_id ? reminderForm.customer_name : customerQuery.trim(),
        product_id: reminderForm.product_id || null,
        product_name: reminderForm.product_id ? reminderForm.product_name : productQuery.trim(),
        quantity: reminderForm.quantity,
        supply_type: reminderForm.supply_type,
        custom_days: reminderForm.custom_days,
        notify: reminderForm.notify,
      }, { headers: getAuthHeader() });

      toast.success(res.data.message || 'Reminder created');
      setIsCreateDialogOpen(false);
      setReminderForm({ customer_id: '', customer_name: '', product_id: '', product_name: '', quantity: 1, supply_type: '30_days', custom_days: 30, notify: 'me' });
      setCustomerQuery('');
      setProductQuery('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create reminder');
    } finally {
      setFormLoading(false);
    }
  };

  const handleMarkSent = async (reminderId) => {
    try {
      await axios.put(`${API_URL}/reminders/${reminderId}/mark-sent`, {}, { headers: getAuthHeader() });
      toast.success('Reminder marked as sent');
      fetchData();
    } catch (error) {
      toast.error('Failed to update reminder');
    }
  };

  const handleDelete = async (reminderId) => {
    if (!window.confirm('Delete this reminder?')) return;
    try {
      await axios.delete(`${API_URL}/reminders/${reminderId}`, { headers: getAuthHeader() });
      toast.success('Reminder deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete reminder');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      sent: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      due: 'bg-red-100 text-red-700 border-red-200',
      pending: 'bg-slate-100 text-slate-700 border-slate-200'
    };
    const icons = {
      sent: <Check className="w-3 h-3 mr-1" />,
      due: <AlertTriangle className="w-3 h-3 mr-1" />,
      pending: <Clock className="w-3 h-3 mr-1" />
    };
    return (
      <Badge variant="outline" className={styles[status] || styles.pending}>
        {icons[status]} {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const generateEmailTemplate = (reminder) => {
    const customer = customers.find(c => c.id === reminder.customer_id);
    return `
Hi ${reminder.customer_name},

Just a friendly reminder that your supply of ${reminder.product_name} may be running low!

You purchased ${reminder.quantity_purchased} units on ${formatDate(reminder.purchase_date)}, which should last approximately ${reminder.supply_days} days.

Would you like to reorder? Visit us in store or reply to this email!

Best regards,
Your Supplement Shop Team
    `.trim();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="customer-reminders-page">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-slate-900">
            Customer Reminders
          </h1>
          <p className="text-slate-500 mt-1">Auto-remind customers when their supply is running low</p>
        </div>
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-orange-500 hover:bg-orange-600" data-testid="create-reminder-button">
              <Plus className="w-4 h-4 mr-2" /> New Reminder
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading text-xl font-bold uppercase">Create Reminder</DialogTitle>
              <DialogDescription>Set up a reminder for a customer's product supply</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateReminder} className="space-y-4">
              <div className="space-y-2">
                <Label>Customer</Label>
                <div className="relative">
                  <Input
                    value={customerQuery}
                    onChange={(e) => {
                      setCustomerQuery(e.target.value);
                      setReminderForm({ ...reminderForm, customer_id: '', customer_name: '' });
                    }}
                    placeholder="Type a name (or pick from the list below)"
                    data-testid="reminder-customer-input"
                    autoComplete="off"
                  />
                  {customerSuggestions.length > 0 && (
                    <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                      {customerSuggestions.map(c => (
                        <button
                          key={c.id}
                          type="button"
                          className="w-full text-left px-3 py-2 hover:bg-orange-50 text-sm"
                          data-testid={`customer-suggestion-${c.id}`}
                          onClick={() => {
                            setReminderForm({ ...reminderForm, customer_id: c.id, customer_name: c.name });
                            setCustomerQuery(c.name);
                          }}
                        >
                          <span className="font-medium">{c.name}</span>
                          {(c.phone || c.email) && <span className="text-slate-400 ml-2 text-xs">{c.phone || c.email}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {reminderForm.customer_id
                  ? <p className="text-xs text-emerald-600">✓ Linked to customer record</p>
                  : customerQuery.trim() && <p className="text-xs text-slate-500">Will be saved as a free-typed name (notify-me only)</p>}
              </div>
              
              <div className="space-y-2">
                <Label>Product</Label>
                <div className="relative">
                  <Input
                    value={productQuery}
                    onChange={(e) => {
                      setProductQuery(e.target.value);
                      setReminderForm({ ...reminderForm, product_id: '', product_name: '' });
                    }}
                    placeholder="Start typing to search products…"
                    data-testid="reminder-product-input"
                    autoComplete="off"
                  />
                  {productResults.length > 0 && (
                    <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                      {productResults.map(p => (
                        <button
                          key={p.id}
                          type="button"
                          className="w-full text-left px-3 py-2 hover:bg-orange-50 text-sm"
                          data-testid={`product-suggestion-${p.id}`}
                          onClick={() => {
                            setReminderForm({ ...reminderForm, product_id: p.id, product_name: p.name });
                            setProductQuery(p.name);
                            setProductResults([]);
                          }}
                        >
                          {p.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {reminderForm.product_id && <p className="text-xs text-emerald-600">✓ Linked to product</p>}
              </div>

              <div className="space-y-2">
                <Label>Who gets the reminder?</Label>
                <Select
                  value={reminderForm.notify}
                  onValueChange={(v) => setReminderForm({ ...reminderForm, notify: v })}
                >
                  <SelectTrigger data-testid="reminder-notify">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="me">Notify me (store) — email me to call/order for them</SelectItem>
                    <SelectItem value="customer">Email the customer directly</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Quantity Purchased</Label>
                  <Input
                    type="number"
                    min="1"
                    value={reminderForm.quantity}
                    onChange={(e) => setReminderForm({ ...reminderForm, quantity: parseInt(e.target.value) || 1 })}
                    data-testid="reminder-quantity"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Supply Duration</Label>
                  <Select
                    value={reminderForm.supply_type}
                    onValueChange={(v) => setReminderForm({ ...reminderForm, supply_type: v })}
                  >
                    <SelectTrigger data-testid="reminder-supply-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="30_days">30 Days (per unit)</SelectItem>
                      <SelectItem value="60_days">60 Days (per unit)</SelectItem>
                      <SelectItem value="90_days">90 Days (per unit)</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {reminderForm.supply_type === 'custom' && (
                <div className="space-y-2">
                  <Label>Custom Days (per unit)</Label>
                  <Input
                    type="number"
                    min="1"
                    value={reminderForm.custom_days}
                    onChange={(e) => setReminderForm({ ...reminderForm, custom_days: parseInt(e.target.value) || 30 })}
                  />
                </div>
              )}

              <div className="p-3 bg-slate-50 rounded-sm text-sm">
                <p className="text-slate-600">
                  When due (≈5 days before the supply runs out), the reminder is <strong>emailed automatically</strong> to {reminderForm.notify === 'customer' ? 'the customer' : 'you'} 
                  {' '}({reminderForm.supply_type === 'custom' 
                    ? reminderForm.custom_days * reminderForm.quantity 
                    : parseInt(reminderForm.supply_type) * reminderForm.quantity || 30 * reminderForm.quantity} days total supply)
                </p>
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={formLoading} className="bg-slate-900">
                  {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Reminder'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Alerts */}
      {dueReminders && dueReminders.total_due > 0 && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4 flex items-center gap-4">
            <Bell className="w-10 h-10 text-red-500" />
            <div>
              <p className="font-bold text-red-700">{dueReminders.total_due} Reminder(s) Due Now!</p>
              <p className="text-sm text-red-600">Send reminder emails to these customers</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs defaultValue="all" className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-3">
          <TabsTrigger value="all">All ({reminders.length})</TabsTrigger>
          <TabsTrigger value="due" className="text-red-600">
            Due ({dueReminders?.due_now?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="upcoming">
            Upcoming ({dueReminders?.upcoming_7_days?.length || 0})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="mt-6">
          <ReminderTable 
            reminders={reminders} 
            onMarkSent={handleMarkSent} 
            onDelete={handleDelete}
            getStatusBadge={getStatusBadge}
            generateEmailTemplate={generateEmailTemplate}
          />
        </TabsContent>

        <TabsContent value="due" className="mt-6">
          <ReminderTable 
            reminders={dueReminders?.due_now || []} 
            onMarkSent={handleMarkSent} 
            onDelete={handleDelete}
            getStatusBadge={getStatusBadge}
            generateEmailTemplate={generateEmailTemplate}
          />
        </TabsContent>

        <TabsContent value="upcoming" className="mt-6">
          <ReminderTable 
            reminders={dueReminders?.upcoming_7_days || []} 
            onMarkSent={handleMarkSent} 
            onDelete={handleDelete}
            getStatusBadge={getStatusBadge}
            generateEmailTemplate={generateEmailTemplate}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

const ReminderTable = ({ reminders, onMarkSent, onDelete, getStatusBadge, generateEmailTemplate }) => {
  const [emailPreview, setEmailPreview] = useState(null);

  return (
    <>
      <Card className="border rounded-sm" data-testid="reminders-table">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="font-heading font-bold uppercase text-xs">Customer</TableHead>
              <TableHead className="font-heading font-bold uppercase text-xs">Product</TableHead>
              <TableHead className="font-heading font-bold uppercase text-xs text-center">Qty</TableHead>
              <TableHead className="font-heading font-bold uppercase text-xs">Purchase Date</TableHead>
              <TableHead className="font-heading font-bold uppercase text-xs">Reminder Date</TableHead>
              <TableHead className="font-heading font-bold uppercase text-xs">Status</TableHead>
              <TableHead className="w-48"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {reminders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                  <Bell className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  No reminders
                </TableCell>
              </TableRow>
            ) : (
              reminders.map((reminder) => (
                <TableRow key={reminder.id} data-testid={`reminder-row-${reminder.id}`}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{reminder.customer_name}</p>
                      <p className="text-xs text-slate-500">{reminder.customer_email || reminder.customer_phone}</p>
                      <p className="text-[11px] text-teal-700">{reminder.notify === 'customer' ? '→ emails customer' : '→ notifies store'}{reminder.sent_via ? ' · auto-sent' : ''}</p>
                    </div>
                  </TableCell>
                  <TableCell>{reminder.product_name}</TableCell>
                  <TableCell className="text-center">{reminder.quantity_purchased}</TableCell>
                  <TableCell>{formatDate(reminder.purchase_date)}</TableCell>
                  <TableCell>
                    <div>
                      <p>{formatDate(reminder.reminder_date)}</p>
                      <p className="text-xs text-slate-500">
                        {reminder.days_until_reminder > 0 
                          ? `In ${reminder.days_until_reminder} days` 
                          : reminder.days_until_reminder === 0 
                            ? 'Today!' 
                            : `${Math.abs(reminder.days_until_reminder)} days ago`}
                      </p>
                    </div>
                  </TableCell>
                  <TableCell>{getStatusBadge(reminder.status)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {!reminder.reminder_sent && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEmailPreview(reminder)}
                            className="text-blue-600"
                          >
                            <Mail className="w-4 h-4 mr-1" /> Preview
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onMarkSent(reminder.id)}
                            className="text-emerald-600"
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => onDelete(reminder.id)}
                        className="text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Email Preview Dialog */}
      <Dialog open={!!emailPreview} onOpenChange={() => setEmailPreview(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase flex items-center gap-2">
              <Mail className="w-5 h-5" /> Email Preview
            </DialogTitle>
            <DialogDescription>
              To: {emailPreview?.customer_name} ({emailPreview?.customer_email || 'No email'})
            </DialogDescription>
          </DialogHeader>
          <div className="bg-slate-50 p-4 rounded-sm">
            <pre className="whitespace-pre-wrap text-sm font-mono">
              {emailPreview && generateEmailTemplate(emailPreview)}
            </pre>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setEmailPreview(null)}>Close</Button>
            <Button 
              onClick={() => {
                if (emailPreview?.customer_email) {
                  window.location.href = `mailto:${emailPreview.customer_email}?subject=Time to Reorder ${emailPreview.product_name}!&body=${encodeURIComponent(generateEmailTemplate(emailPreview))}`;
                } else {
                  toast.error('No email address for this customer');
                }
              }}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Send className="w-4 h-4 mr-2" /> Open in Email Client
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CustomerReminders;
